import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LanguageSwitcher from './../i18n/components/LanguageSwitcher/LanguageSwitcher';
import Banner from './PromoPageComponent/Banner/Banner';
import logoWhite from './../assets/images/logo__white.svg';
import { useTranslation } from 'react-i18next';
import HeaderSkeleton from './HeaderSkeleton';
import { useFontAwesomeIcon } from '../hooks/useFontAwesomeIcon';
import { useHeaderFooterCache } from '../hooks/useHeaderFooterCache';
import LocalizedNavLink from '../hooks/LocalizedNavLink';
import './Header.scss';

const Header = ({ hideLanguageSwitcher = false }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const { getIcon } = useFontAwesomeIcon();

  const { getHeaderData, isLoading, hasError } = useHeaderFooterCache();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isTV, setIsTV] = useState(false);
  const sidebarRef = useRef(null);
  const burgerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isContactPage = React.useMemo(() => {
    return location.pathname === '/contact' ||
      location.pathname === `/${currentLang}/contact` ||
      location.pathname === '/contacts' ||
      location.pathname === `/${currentLang}/contacts`;
  }, [location.pathname, currentLang]);

  useEffect(() => {
    const checkTV = () => {
      setIsTV(window.innerWidth >= 1920);
    };

    checkTV();
    window.addEventListener('resize', checkTV);

    return () => window.removeEventListener('resize', checkTV);
  }, []);

  const headerData = getHeaderData(currentLang);

  const updateBannerVisibility = useCallback(() => {
    const banner = document.querySelector('.hidden__banner');
    setIsBannerVisible(banner && banner.offsetHeight > 0);
  }, []);

  useEffect(() => {
    updateBannerVisibility();

    const observer = new MutationObserver(updateBannerVisibility);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => observer.disconnect();
  }, [updateBannerVisibility]);

  useEffect(() => {
    const timer = setTimeout(updateBannerVisibility, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, updateBannerVisibility]);

  const headerTop = isBannerVisible
    ? (isTV ? '100px' : '70px')
    : '0px';

  const toggleMenu = useCallback((event) => {
    event.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleLogoClick = useCallback((event) => {
    event.preventDefault();

    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.dispatchEvent(new CustomEvent('refreshHomeContent'));
    } else {
      navigate('/');
    }

    closeMenu();
  }, [location.pathname, navigate, closeMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        burgerRef.current &&
        !burgerRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeMenu]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) {
    return <HeaderSkeleton />;
  }

  if (hasError || !headerData?.isValid) {
    return null;
  }

  const { mainContent, generalContent } = headerData;
  const validCompanyLinks = generalContent.Company.filter(item => item.title && item.path);

  return (
    <>
      <Banner />
      <header className='header' style={{ top: headerTop }}>
        <div className='header__logo__item'>
          <LocalizedNavLink to={mainContent.HeaderLogo.link} className='header__logo__link' onClick={handleLogoClick}>
            <img className='logo' src={logoWhite} alt='Carabinex' />
          </LocalizedNavLink>
        </div>
        <div className='header__burger__item'>
          {!isContactPage && (
            <div className={`header__link__wrapper ${isScrolled ? 'visible' : ''}`}>
              <LocalizedNavLink className='header__link' to={mainContent.HeaderButton.link} onClick={closeMenu}>
                {mainContent.HeaderButton.text}
              </LocalizedNavLink>
            </div>
          )}
          <div className={`temp ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu} ref={burgerRef}>
            <span className="burger"></span>
            <span className="burger"></span>
            <span className="burger"></span>
          </div>
        </div>
        <LanguageSwitcher hideLanguageSwitcher={hideLanguageSwitcher} />
        <div className={`sidebar ${isMenuOpen ? 'sidebar--open' : ''}`} ref={sidebarRef}>
          <div className='sidebar__buttons'>
            <div className='sidebar__link__wrapper' style={{ top: headerTop }}>
              <LocalizedNavLink className='sidebar__link' to={mainContent.HeaderButton.link} onClick={closeMenu}>
                {mainContent.HeaderButton.text}
              </LocalizedNavLink>
            </div>
          </div>
          <nav className='nav'>
            <ul className='nav__list'>
              {validCompanyLinks.map((link, index) => (
                <li key={index} className="list__item">
                  <LocalizedNavLink
                    className="nav__item__link"
                    to={link.path}
                    onClick={closeMenu}
                  >
                    {link.title}
                  </LocalizedNavLink>
                </li>
              ))}
            </ul>
            <div className='socials__wrapper'>
              {Object.values(generalContent.SocialLinks).map((social) => (
                <div key={social.id} className="socials__item">
                  <a
                    className="socials__link"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={social.shareUrl}
                    aria-label={social.name}
                  >
                    {getIcon(social.iconType, social.iconClass)}
                  </a>
                </div>
              ))}
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};

export default React.memo(Header);